/**
 * DAGControlButtons Component - Execution Control Interface
 *
 * FERP CRITICAL: Domain-agnostic execution controls
 * Life-critical emergency controls (pause/cancel)
 *
 * Principles:
 * - All data from backend via useDAGExecution hook (no hardcoded values)
 * - Repository pattern (GraphQL mutations only)
 * - Emergency controls with confirmation dialogs
 * - Immutable audit trails for all control actions
 * - Backend ID generation (never generate execution IDs client-side)
 * - Life-critical safety validation
 * - 16 primitive types (DAG is workflow execution primitive)
 */

import React, { useState } from 'react';
import { Button, Group, Modal, Stack, Text, Alert } from '@mantine/core';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconX,
  IconRefresh,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  DAGExecutionStatusEnum,
  UseDAGExecutionReturn,
} from './hooks/useDAGExecution';

// ============================================================
// TypeScript Types
// ============================================================

export interface DAGControlButtonsProps {
  executionId: string;
  currentStatus: DAGExecutionStatusEnum | null;
  pauseExecution: UseDAGExecutionReturn['pauseExecution'];
  resumeExecution: UseDAGExecutionReturn['resumeExecution'];
  cancelExecution: UseDAGExecutionReturn['cancelExecution'];
  replayExecution: UseDAGExecutionReturn['replayExecution'];
  disabled?: boolean;
}

// ============================================================
// Component Implementation
// ============================================================

export function DAGControlButtons({
  executionId,
  currentStatus,
  pauseExecution,
  resumeExecution,
  cancelExecution,
  replayExecution,
  disabled = false,
}: DAGControlButtonsProps) {
  const [isPauseConfirmOpen, setIsPauseConfirmOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isReplayConfirmOpen, setIsReplayConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle pause execution
  const handlePause = async () => {
    setIsProcessing(true);
    try {
      await pauseExecution(executionId);
    } finally {
      setIsProcessing(false);
      setIsPauseConfirmOpen(false);
    }
  };

  // Handle resume execution
  const handleResume = async () => {
    setIsProcessing(true);
    try {
      await resumeExecution(executionId);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel execution
  const handleCancel = async () => {
    setIsProcessing(true);
    try {
      await cancelExecution(executionId);
    } finally {
      setIsProcessing(false);
      setIsCancelConfirmOpen(false);
    }
  };

  // Handle replay execution
  const handleReplay = async () => {
    setIsProcessing(true);
    try {
      await replayExecution(executionId);
    } finally {
      setIsProcessing(false);
      setIsReplayConfirmOpen(false);
    }
  };

  // Determine which buttons to show based on current status
  const canPause = currentStatus === DAGExecutionStatusEnum.Running;
  const canResume = currentStatus === DAGExecutionStatusEnum.Paused;
  const canCancel =
    currentStatus === DAGExecutionStatusEnum.Running ||
    currentStatus === DAGExecutionStatusEnum.Paused;
  const canReplay =
    currentStatus === DAGExecutionStatusEnum.Completed ||
    currentStatus === DAGExecutionStatusEnum.Failed ||
    currentStatus === DAGExecutionStatusEnum.Cancelled;

  return (
    <>
      <Group gap="sm">
        {/* Pause Button */}
        {canPause && (
          <Button
            leftSection={<IconPlayerPause size={16} />}
            color="blue"
            variant="filled"
            onClick={() => setIsPauseConfirmOpen(true)}
            disabled={disabled || isProcessing}
            loading={isProcessing}
          >
            Pause
          </Button>
        )}

        {/* Resume Button */}
        {canResume && (
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            color="green"
            variant="filled"
            onClick={handleResume}
            disabled={disabled || isProcessing}
            loading={isProcessing}
          >
            Resume
          </Button>
        )}

        {/* Cancel Button */}
        {canCancel && (
          <Button
            leftSection={<IconX size={16} />}
            color="red"
            variant="filled"
            onClick={() => setIsCancelConfirmOpen(true)}
            disabled={disabled || isProcessing}
            loading={isProcessing}
          >
            Cancel
          </Button>
        )}

        {/* Replay Button */}
        {canReplay && (
          <Button
            leftSection={<IconRefresh size={16} />}
            color="gray"
            variant="outline"
            onClick={() => setIsReplayConfirmOpen(true)}
            disabled={disabled || isProcessing}
            loading={isProcessing}
          >
            Replay
          </Button>
        )}
      </Group>

      {/* Pause Confirmation Modal */}
      <Modal
        opened={isPauseConfirmOpen}
        onClose={() => setIsPauseConfirmOpen(false)}
        title="Pause Execution"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              Pausing execution will stop all running nodes. The execution can be resumed later.
            </Text>
          </Alert>

          <Text size="sm">Execution ID: {executionId}</Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setIsPauseConfirmOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handlePause}
              loading={isProcessing}
              disabled={isProcessing}
            >
              Confirm Pause
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        opened={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
        title="Cancel Execution"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            <Text size="sm">
              Canceling execution will stop all running nodes and mark the execution as cancelled.
              This action cannot be undone.
            </Text>
          </Alert>

          <Text size="sm">Execution ID: {executionId}</Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setIsCancelConfirmOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleCancel}
              loading={isProcessing}
              disabled={isProcessing}
            >
              Confirm Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Replay Confirmation Modal */}
      <Modal
        opened={isReplayConfirmOpen}
        onClose={() => setIsReplayConfirmOpen(false)}
        title="Replay Execution"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              Replaying execution will create a new execution instance with the same workflow definition.
              This is useful for debugging or reprocessing failed workflows.
            </Text>
          </Alert>

          <Text size="sm">Source Execution ID: {executionId}</Text>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setIsReplayConfirmOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleReplay}
              loading={isProcessing}
              disabled={isProcessing}
            >
              Confirm Replay
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
